/**
 * G8. Болезнь: «Очередь». При решении `health.treatment` — пять окошек
 * поликлиники, у каждого время ожидания, цена (доля полной цены лечения
 * из решения: TREAT_COST_GDP · ВВП на душу · oopHealth) и здоровье:
 * частный врач +15 за полную цену, платная очередь +15 за половину,
 * недорогое окно +8 за пятую часть, бесплатное +8 без денег, «уйти» −5.
 * Бесплатное окно в бедных странах (ВВП на душу < CLINIC_POOR_GDP) может
 * закрыться с p = CLINIC_FREE_CLOSED_P — «врача нет», и это −5, как без
 * лечения. Вероятность написана на окне.
 *
 * Исход — вариант решения (`treat`/`wait`) и флаги, которые решение
 * читает вместо своих констант: `flags.treatmentHealth` и
 * `flags.treatmentCostShare` (движок, decisions/defs.ts). «Пропустить» —
 * лучшее окно по деньгам без жребия: частное, если хватает, иначе недорогое,
 * иначе бесплатное, если оно точно открыто, иначе «уйти».
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { getValue } from '@life/data';
import { FLAG_TREATMENT_COST_SHARE, FLAG_TREATMENT_HEALTH, TREAT_COST_GDP, type Life, type MinigameOutcome, type Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { gdpPpp } from '../casino/common';
import { pendingOf } from '../skill/common';
import { nothing } from './common';

export const CLINIC_WINDOWS = 5;
/** ВВП на душу (ППС), ниже которого бесплатное окно может закрыться. */
export const CLINIC_POOR_GDP = 5000;
export const CLINIC_FREE_CLOSED_P = 0.5;
export const CLINIC_CLOSED_HEALTH = -5;

export interface ClinicWindowDef {
  id: 'private' | 'paid' | 'cheap' | 'free' | 'none';
  waitDays: number;
  /** Доля полной цены лечения из решения. */
  costShare: number;
  health: number;
}

export const CLINIC_WINDOW_DEFS: readonly ClinicWindowDef[] = [
  { id: 'private', waitDays: 1, costShare: 1, health: 15 },
  { id: 'paid', waitDays: 14, costShare: 0.5, health: 15 },
  { id: 'cheap', waitDays: 30, costShare: 0.2, health: 8 },
  { id: 'free', waitDays: 60, costShare: 0, health: 8 },
  { id: 'none', waitDays: 0, costShare: 0, health: -5 },
];

export interface ClinicWindow {
  id: ClinicWindowDef['id'];
  key: string;
  waitDays: number;
  costShare: number;
  /** Доллары ППС. */
  cost: number;
  health: number;
  /** Вероятность «врача нет» (тогда здоровье −5). */
  closedP: number;
}

export interface ClinicState {
  windows: ClinicWindow[];
  /** Полная цена лечения в долларах ППС. */
  baseCost: number;
  money: number;
  timeoutOption: string;
}

export type ClinicInput = { pick: number };

const CLINIC_KINDS = ['health.treatment'] as const;

export function clinicWindowKey(id: string): string {
  return `minigame.clinic.window.${id}`;
}

/** Полная цена лечения — как в решении `health.treatment`. */
export function clinicBaseCost(ds: Dataset, life: Life): number {
  const oop = getValue(ds, life.iso3, 'oopHealth', life.birthYear + life.age).v;
  return Math.max(0, TREAT_COST_GDP * gdpPpp(ds, life) * (oop / 100));
}

function treated(w: ClinicWindow): MinigameOutcome {
  return {
    ...nothing(),
    decisionOption: 'treat',
    flags: { [FLAG_TREATMENT_HEALTH]: w.health, [FLAG_TREATMENT_COST_SHARE]: w.costShare },
    logKey: 'minigame.clinic.treated',
    logParams: { days: w.waitDays },
  };
}

function closed(): MinigameOutcome {
  return { ...nothing(), decisionOption: 'wait', logKey: 'minigame.clinic.closed' };
}

function left(): MinigameOutcome {
  return { ...nothing(), decisionOption: 'wait', logKey: 'minigame.clinic.left' };
}

export const clinic: MinigameDef<ClinicInput, ClinicState> = {
  id: 'clinic',
  section: 'G',
  triggers: [{ kind: 'decision', decision: 'health.treatment' }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return pendingOf(life, CLINIC_KINDS) !== null;
  },
  setup(life: Life, ds: Dataset): ClinicState {
    const baseCost = clinicBaseCost(ds, life);
    const poor = gdpPpp(ds, life) < CLINIC_POOR_GDP;
    const windows = CLINIC_WINDOW_DEFS.map((w) => ({
      id: w.id,
      key: clinicWindowKey(w.id),
      waitDays: w.waitDays,
      costShare: w.costShare,
      cost: w.costShare * baseCost,
      health: w.health,
      closedP: w.id === 'free' && poor ? CLINIC_FREE_CLOSED_P : 0,
    }));
    return { windows, baseCost, money: Math.max(0, life.money), timeoutOption: pendingOf(life, CLINIC_KINDS)?.timeoutOption ?? 'wait' };
  },
  play(state: ClinicState, input: ClinicInput, _life: Life, rng: Rng): MinigameOutcome {
    const w = Number.isInteger(input.pick) ? state.windows[input.pick] : undefined;
    if (!w || w.id === 'none') return left();
    if (w.closedP > 0 && rng.chance(w.closedP)) return closed();
    return treated(w);
  },
  expected(state: ClinicState): MinigameOutcome {
    const open = state.windows.filter((w) => w.id !== 'none' && w.closedP === 0 && w.cost <= state.money);
    if (open.length === 0) return left();
    const best = open.reduce((a, b) => (b.health > a.health || (b.health === a.health && b.cost < a.cost) ? b : a));
    return treated(best);
  },
};
